/**
 * ChartsWebSocketHandler - Receives bar and marketAggregate messages from charts WebSocket
 * and updates StreamsContext. Must be inside StreamsContextProvider and WebSocketProvider.
 */
import { useContext, useEffect, useCallback, useRef } from 'react';
import { StreamsContext } from '../contexts/StreamsContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { BannerContext } from '../contexts/BannerContext';
import {
  onStreamRecovered,
  reportStreamError,
  shouldDeferStreamErrorBanner,
} from '../utils/streamErrorBanner';
import { mergeBars } from '../utils/chartBarMerge';
import { canonicalOptionSymbol } from '../utils/optionSymbol';
import { aggregateDepthSide, sanitizeBook } from '../hooks/marketDepthBook';

const aggregateSide = aggregateDepthSide;

function optionSpreadsFromFrame(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data)) return data.filter((row) => row?.Legs?.[0]?.Symbol);
  if (Array.isArray(data.Spreads)) return data.Spreads.filter((row) => row?.Legs?.[0]?.Symbol);
  if (data.Legs?.[0]?.Symbol) return [data];
  return [];
}

export default function ChartsWebSocketHandler() {
  const { addChartsListener, chartsReady } = useWebSocket();
  const {
    setBarsByKey,
    setBarStreamHealthy,
    setBarsStreamMetaByKey,
    setMarketAggregates,
    setLiquidityStreamHealthy,
    setOptionChainsByKey,
  } = useContext(StreamsContext);
  const { showMessage } = useContext(BannerContext);
  const barHealthyRef = useRef(true);
  const liquidityHealthyRef = useRef(true);
  const chartsReadyRef = useRef(chartsReady);

  useEffect(() => {
    chartsReadyRef.current = chartsReady;
    if (!chartsReady) barHealthyRef.current = false;
  }, [chartsReady]);

  const handleMessage = useCallback((msg) => {
    if (!msg || msg.error) return;

    if (msg.channel === 'bars') {
      const { symbol, timeframe, unit, sessionTemplate, data } = msg;
      const unitNorm = unit || 'Minute';
      const sessionNorm = sessionTemplate || 'Default';
      const resolveBarsKey = (prevMap) => {
        const exact = [symbol, timeframe, unitNorm, sessionNorm].join('|');
        if (prevMap?.[exact]) return exact;
        const prefix = `${symbol}|${timeframe}|${unitNorm}|`;
        const matches = Object.keys(prevMap || {}).filter((k) => k.startsWith(prefix));
        if (matches.length === 1) return matches[0];
        if (matches.length > 1) {
          const bySession = matches.find((k) => k.endsWith(`|${sessionNorm}`));
          if (bySession) return bySession;
          if (sessionNorm === 'Default') {
            const nonDefault = matches.filter((k) => !k.endsWith('|Default'));
            if (nonDefault.length === 1) return nonDefault[0];
          }
        }
        return exact;
      };

      const tradesDown = data?.TradesStream === 'down';
      const markBarsHealthy = () => {
        if (tradesDown) {
          barHealthyRef.current = false;
          setBarStreamHealthy(false);
          return;
        }
        barHealthyRef.current = true;
        onStreamRecovered('bars');
        setBarStreamHealthy(true);
      };

      if (data?.LateJoin) {
        markBarsHealthy();
        return;
      }
      if (data?.Heartbeat) {
        markBarsHealthy();
        return;
      }
      if (data?.Error) {
        barHealthyRef.current = false;
        setBarStreamHealthy(false);
        const message = data.Message || data.Error;
        reportStreamError({
          streamKey: 'bars',
          message,
          showMessage,
          defer: shouldDeferStreamErrorBanner(data, chartsReadyRef.current),
          isHealthy: () => barHealthyRef.current,
        });
        return;
      }
      const markBarsReady = (prev) => {
        const barsKey = resolveBarsKey(prev);
        setBarsStreamMetaByKey((metaPrev) => {
          if (metaPrev[barsKey]?.status === 'ready') return metaPrev;
          return { ...metaPrev, [barsKey]: { status: 'ready' } };
        });
        return barsKey;
      };

      if (data?.StreamStatus === 'Partial') {
        // History snapshot failed after a retry. Keep any bars we have so the chart is usable
        // (scrolling back retries history); only surface an error when there is nothing to draw.
        setBarsByKey((prev) => {
          const barsKey = resolveBarsKey(prev);
          if ((prev[barsKey] || []).length > 0) {
            markBarsReady(prev);
          } else {
            setBarsStreamMetaByKey((metaPrev) => {
              if (metaPrev[barsKey]?.status === 'ready') return metaPrev;
              return {
                ...metaPrev,
                [barsKey]: { status: 'error', message: data.Message || 'Chart history unavailable.' },
              };
            });
          }
          return prev;
        });
        return;
      }
      if (data?.StreamStatus === 'EndSnapshot') {
        markBarsHealthy();
        setBarsByKey((prev) => {
          markBarsReady(prev);
          return prev;
        });
        return;
      }
      if (data?.TimeStamp) {
        markBarsHealthy();
        setBarsByKey((prev) => {
          const barsKey = resolveBarsKey(prev);
          const existing = prev[barsKey] || [];
          const merged = mergeBars(existing, [data], { interval: timeframe, unit: unitNorm });
          return { ...prev, [barsKey]: merged };
        });
      }
    } else if (msg.channel === 'marketAggregates') {
      const symbol = String(msg.symbol || '').trim().toUpperCase();
      const data = msg.data;
      if (!symbol || !data) return;

      if (data.LateJoin) {
        return;
      }
      if (data.Heartbeat) {
        liquidityHealthyRef.current = true;
        onStreamRecovered(`liquidity-${symbol}`);
        setLiquidityStreamHealthy(true);
        return;
      }

      if (data.Error || data.Message) {
        const m = String(data.Message || data.Error || '');
        const isNotEntitled = m.toLowerCase().includes('not entitled');
        liquidityHealthyRef.current = false;
        if (typeof m === 'string' && m.toLowerCase().includes('symbol is not supported for this endpoint')) {
          showMessage?.(`Liquidity not available for ${symbol}. TradeStation does not support market depth for it.`, 'warning', true, 'banner');
        } else if (isNotEntitled) {
          showMessage?.(
            `Liquidity data for ${symbol} requires Level 2 access. Check your TradeStation data subscription or disable the Liquidity overlay.`,
            'warning',
            true,
            'notification'
          );
        } else if (m) {
          reportStreamError({
            streamKey: `liquidity-${symbol}`,
            message: m,
            showMessage,
            defer: shouldDeferStreamErrorBanner(data, chartsReadyRef.current),
            isHealthy: () => liquidityHealthyRef.current,
            mode: 'banner',
          });
        }
        setLiquidityStreamHealthy(false);
        return;
      }

      liquidityHealthyRef.current = true;
      onStreamRecovered(`liquidity-${symbol}`);
      setLiquidityStreamHealthy(true);

      if (Array.isArray(data.Bids) || Array.isArray(data.Asks)) {
        const nextBids = aggregateSide(data.Bids || []);
        const nextAsks = aggregateSide(data.Asks || []);
        setMarketAggregates((prev) => ({
          ...prev,
          [symbol]: sanitizeBook({ bids: nextBids, asks: nextAsks }),
        }));
        return;
      }

      if (data.Side && data.Price != null) {
        const side = String(data.Side).toLowerCase();
        const isBid = side === 'bid' || side === 'bids';
        const priceKey = String(data.Price);
        const sizeNum = Number(data.TotalSize ?? data.Size ?? 0);
        setMarketAggregates((prev) => {
          const currentData = prev[symbol] || { bids: [], asks: [] };
          const patch = (levels) => {
            const next = (Array.isArray(levels) ? levels : []).filter((lvl) => String(lvl.Price) !== priceKey);
            if (Number.isFinite(sizeNum) && sizeNum > 0) {
              next.push(aggregateSide([data])[0]);
            }
            return next;
          };
          return {
            ...prev,
            [symbol]: sanitizeBook({
              bids: isBid ? patch(currentData.bids) : currentData.bids,
              asks: isBid ? currentData.asks : patch(currentData.asks),
            }, { keep: isBid ? 'bid' : 'ask' }),
          };
        });
        return;
      }

      if (Array.isArray(data.bids) || Array.isArray(data.asks)) {
        setMarketAggregates((prev) => ({
          ...prev,
          [symbol]: sanitizeBook({
            bids: aggregateSide(data.bids || []),
            asks: aggregateSide(data.asks || []),
          }),
        }));
      }
    } else if (msg.channel === 'optionChain') {
      const underlying = String(msg.underlying || msg.symbol || '').toUpperCase();
      const expiration = String(msg.expiration || '').slice(0, 10);
      const proximity = msg.strikeProximity != null ? String(msg.strikeProximity) : '';
      const chainKey = `${underlying}|${expiration}|${proximity}`;
      const data = msg.data;
      if (!underlying || !expiration || !data || data.Heartbeat) return;

      const spreads = optionSpreadsFromFrame(data);
      if (spreads.length > 0) {
        setOptionChainsByKey((prev) => {
          const current = prev[chainKey] || { rows: {}, settled: false, rowCount: null, unsupported: false, error: null };
          const rows = { ...current.rows };
          for (const spread of spreads) {
            const contract = canonicalOptionSymbol(spread?.Legs?.[0]?.Symbol);
            if (contract) rows[contract] = spread;
          }
          return { ...prev, [chainKey]: { ...current, rows, error: null } };
        });
      }

      const isTerminal = data.StreamStatus === 'EndSnapshot' || data.Unsupported || (data.Error && spreads.length === 0);
      if (!isTerminal) return;

      setOptionChainsByKey((prev) => {
        const current = prev[chainKey] || { rows: {} };
        const stored = Object.keys(current.rows || {}).length;
        const reported = data.RowCount != null ? Number(data.RowCount) : stored;
        return {
          ...prev,
          [chainKey]: {
            ...current,
            settled: true,
            rowCount: stored > 0 ? stored : reported,
            unsupported: Boolean(data.Unsupported),
            error: stored > 0 ? null : (data.Error || data.Message || null),
          },
        };
      });
    }
  }, [setBarsByKey, setBarStreamHealthy, setBarsStreamMetaByKey, setMarketAggregates, setLiquidityStreamHealthy, setOptionChainsByKey, showMessage]);

  useEffect(() => {
    if (!chartsReady) return;
    const remove = addChartsListener(handleMessage);
    return remove;
  }, [chartsReady, addChartsListener, handleMessage]);

  return null;
}
